import * as React from 'react';
import Modal from '@material-ui/core/Modal';
import { Scrollbars } from 'react-custom-scrollbars';
import useCustomViews from './useCustomViews';
import OutlinedInput from '../../common/outlined-input';
import { Button, CircularProgress } from '@material-ui/core';
import * as styles from '../report.module.scss';
import MaterialIcon from '@material/react-material-icon';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { AppContext, AppProviderDispatch } from '../../../shared/app-context';
import CustomViewList from './custom-view-list';
import OtherViewList from './other-view-list';
import EditingView from './editing-view';

interface Props {
  open: boolean;
  viz: any;
  isInteractive: boolean;
  onClose: () => void;
}

/**
 * @function CustomViewsModal - Modal to show custom views management tools
 */
const CustomViewsModal = ({ open, onClose, viz, isInteractive }: Props) => {
  const [loadError, setLoadError] = React.useState<string>(null);
  const { state } = React.useContext<AppProviderDispatch>(AppContext);
  const [newViewName, setNewViewName] = React.useState('');
  const [isDefault, setIsDefault] = React.useState<boolean>(false);
  const [isPublic, setIsPublic] = React.useState<boolean>(false);
  const [isManaging, setIsManaging] = React.useState<boolean>(false);

  const handleClose = () => {
    setNewViewName('');
    onClose();
  };

  const { customViews, fetchCustomViews, setCustomViews, addCustomView, showCustomView, loading } = useCustomViews(
    viz,
    handleClose,
    setLoadError
  );

  React.useEffect(() => {
    if (!open) {
      setIsManaging(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (isInteractive) {
      fetchCustomViews();
    }
  }, [isInteractive]);

  const renderDefault = () => {
    return (
      <React.Fragment>
        <form
          className={styles.viewNameForm}
          onSubmit={(event) => addCustomView(event, newViewName, isDefault, isPublic)}
        >
          <OutlinedInput
            id="outlined-view-name"
            label="View Name"
            margin="dense"
            variant="outlined"
            onChange={(event) => setNewViewName(event.target.value)}
            value={newViewName}
            tabIndex={1}
            className={styles.viewNameInput}
            inputProps={{ maxLength: 1024 }}
          />
          <Button
            type="submit"
            title="Submit"
            className={!newViewName ? styles.disabledSubmit : styles.submitButton}
            variant="contained"
            color="primary"
            disabled={!newViewName}
          >
            Save
          </Button>
        </form>
        <FormGroup row classes={{ root: styles.checkboxForm }}>
          <FormControlLabel
            classes={{ root: styles.checked }}
            control={
              <Checkbox
                checked={isDefault}
                classes={{ root: isDefault ? styles.checkboxActive : styles.checkbox }}
                onChange={() => setIsDefault((checked) => !checked)}
              />
            }
            label="Make it my default"
          />
          <FormControlLabel
            classes={{ root: styles.checked }}
            control={
              <Checkbox
                checked={isPublic}
                classes={{ root: isPublic ? styles.checkboxActive : styles.checkbox }}
                onChange={() => setIsPublic((checked) => !checked)}
              />
            }
            label="Make it public"
          />
        </FormGroup>
        <div className={styles.customViews}>
          <span className="label2">My Views</span>
          <div className={styles.viewsContainer}>
            {customViews.length > 0 ? (
              <CustomViewList
                customViews={customViews}
                activeCustomView={viz.workbook.getCustomViewsAsync()}
                showCustomView={showCustomView}
              />
            ) : (
              <span className={styles.viewName} style={{ margin: 'auto' }}>
                Nothing Saved Yet
              </span>
            )}
          </div>
        </div>
        <div className={styles.otherViews}>
          <span className="label2">Other Views</span>
          <div className={styles.viewsContainer}>
            {customViews.length > 0 ? (
              <OtherViewList
                customViews={customViews}
                activeCustomView={viz.workbook.getCustomViewsAsync()}
                showCustomView={showCustomView}
              />
            ) : (
              <span className={styles.viewName} style={{ margin: 'auto' }}>
                Nothing Saved Yet
              </span>
            )}
          </div>
        </div>
        <Button
          className={customViews.length > 0 ? styles.manageButton : styles.disabledManageButton}
          disabled={customViews.length === 0}
          color="primary"
          variant="contained"
          onClick={() => setIsManaging(true)}
        >
          Manage
        </Button>
      </React.Fragment>
    );
  };

  const renderManageViews = () => {
    return (
      <React.Fragment>
        <div className={styles.manageCustomContainer}>
          <span className={styles.manageTitle}>Manage</span>
          {customViews.length > 0 ? (
            <Scrollbars className={styles.manageScroll} autoHide>
              {customViews.map((view) => {
                if (state.email.toLowerCase() === view.ownerEmail) {
                  return (
                    <EditingView
                      key={view.name}
                      viz={viz}
                      view={view}
                      customViews={customViews}
                      otherView={false}
                      setCustomViews={setCustomViews}
                    />
                  );
                }
              })}
            </Scrollbars>
          ) : (
            <span className={styles.viewName} style={{ margin: 'auto' }}>
              Nothing Saved Yet
            </span>
          )}
        </div>
        <div className={styles.manageCustomContainer}>
          <span className={styles.manageTitle}>Other Views</span>
          {customViews.length > 0 ? (
            <Scrollbars className={styles.manageOtherScroll} autoHide>
              <React.Fragment>
                <EditingView viz={viz} customViews={customViews} otherView={true} setCustomViews={setCustomViews} />
                {customViews.map((view) => {
                  if (view.isPublic && state.email.toLowerCase() !== view.ownerEmail) {
                    return (
                      <EditingView
                        key={view.name}
                        viz={viz}
                        view={view}
                        customViews={customViews}
                        otherView={true}
                        setCustomViews={setCustomViews}
                      />
                    );
                  }
                })}
              </React.Fragment>
            </Scrollbars>
          ) : (
            <span className={styles.viewName} style={{ margin: 'auto' }}>
              Nothing Saved Yet
            </span>
          )}
        </div>
        <Button
          className={styles.manageButton}
          variant="contained"
          color="primary"
          onClick={() => setIsManaging(false)}
        >
          Done
        </Button>
      </React.Fragment>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.circularProgressContainer}>
          <CircularProgress />
        </div>
      );
    } else if (loadError) {
      return (
        <div className={styles.loadErrorContainer}>
          <p className={styles.loadError}>{loadError}</p>
        </div>
      );
    } else if (isManaging) {
      return renderManageViews();
    }
    return renderDefault();
  };

  return (
    <Modal className={styles.viewsModal} open={open} onClose={handleClose}>
      <div className={styles.outerContainer}>
        <div className={styles.modalHeader}>
          <h1>Custom Views</h1>
          <MaterialIcon icon="close" title="Close" className={styles.close} onClick={handleClose} />
        </div>
        {renderContent()}
      </div>
    </Modal>
  );
};

export default CustomViewsModal;
